// Reads a .cube file (the 3D colour look-up table that Premiere, DaVinci Resolve, Photoshop and most "film look" packs use) into { size, data: Float32Array (red changes fastest) }.

export const MAX_LUT_SIZE = 65;

// Throws an Error with a message for the visitor when the file is not a usable 3D table.
export function parseCube(text) {
  const lines = String(text || "").replace(/\r/g, "").split("\n");
  let size = 0;
  let title = "";
  let domainMin = [0, 0, 0];
  let domainMax = [1, 1, 1];
  const values = [];
  for (const raw of lines) {
    const line = raw.trim();
    if (!line || line.startsWith("#")) continue;
    if (/^TITLE/i.test(line)) { title = line.replace(/^TITLE\s*/i, "").replace(/^"|"$/g, ""); continue; }
    if (/^LUT_1D_SIZE/i.test(line)) throw new Error("That is a 1D look-up table. Use a 3D one (a .cube file with LUT_3D_SIZE).");
    if (/^LUT_3D_SIZE/i.test(line)) { size = Number(line.split(/\s+/)[1]); continue; }
    if (/^DOMAIN_MIN/i.test(line)) { domainMin = line.split(/\s+/).slice(1, 4).map(Number); continue; }
    if (/^DOMAIN_MAX/i.test(line)) { domainMax = line.split(/\s+/).slice(1, 4).map(Number); continue; }
    if (/^[A-Z_]+/i.test(line) && !/^[-+.\d]/.test(line)) continue;                       // another keyword: not needed
    const parts = line.split(/\s+/).map(Number);
    if (parts.length >= 3 && parts.slice(0, 3).every(Number.isFinite)) values.push(parts[0], parts[1], parts[2]);
  }
  if (!Number.isInteger(size) || size < 2) throw new Error("That file is not a 3D look-up table (.cube): it has no LUT_3D_SIZE.");
  if (size > MAX_LUT_SIZE) throw new Error(`That table is larger than ${MAX_LUT_SIZE}×${MAX_LUT_SIZE}×${MAX_LUT_SIZE}, which this tool does not take.`);
  if (values.length !== size ** 3 * 3) throw new Error(`That table is incomplete: it should have ${size ** 3} colours and has ${Math.floor(values.length / 3)}.`);
  const data = new Float32Array(values.length);
  for (let i = 0; i < values.length; i += 1) {
    const span = domainMax[i % 3] - domainMin[i % 3] || 1;
    data[i] = Math.min(1, Math.max(0, (values[i] - domainMin[i % 3]) / span));            // a table with another domain is stretched to 0..1
  }
  return { size, data, title };
}

// A table that changes nothing (for tests, and as the starting point of made-up looks).
export function identityLut(size = 17) {
  const data = new Float32Array(size ** 3 * 3);
  for (let b = 0; b < size; b += 1) for (let g = 0; g < size; g += 1) for (let r = 0; r < size; r += 1) {
    const i = ((b * size + g) * size + r) * 3;
    data[i] = r / (size - 1); data[i + 1] = g / (size - 1); data[i + 2] = b / (size - 1);
  }
  return { size, data, title: "" };
}
